import React, { useMemo } from "react";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { classNames } from "../../../utils/common/styling";
import { checkIsArrayEmpty } from "../../../utils/common/dataTypes/array";
import { useCreatorThemeContext } from "../../../features/Common/modules/Theme/CreatorThemeContext";
import { getAudioStyles } from "./PostAudio.styles";
import { IPostAudioProps, ISingleAudioRowProps } from "./PostAudio.interface";
import {
  DEFAULT_PRIMARY_COLOR,
  POST_AUDIO_TEXTS,
  WAVE_BAR_HEIGHTS,
} from "./constants/PostAudio.constants";
import { usePostAudioPlayback } from "./hooks/usePostAudioPlayback";
import { formatAudioTime } from "./utils/PostAudio.utils";
import { IPostAudio } from "../Post/Post.types";

const FallbackWaveformPlayer = ({
  audio,
  styles,
  primaryColor,
}: {
  audio: IPostAudio;
  styles: ReturnType<typeof getAudioStyles>;
  primaryColor: string;
}) => {
  const {
    audioRef,
    isPlaying,
    currentTime,
    duration,
    progressPercent,
    togglePlay,
    handleTimeUpdate,
    handleLoadedMetadata,
    handleEnded,
    handleSeek,
    setIsPlaying,
  } = usePostAudioPlayback();

  const handleWaveClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickRatio = Math.max(0, Math.min(1, clickX / rect.width));
    handleSeek(clickRatio * duration);
  };

  return (
    <div className={styles.fallbackWaveContainer}>
      <audio
        ref={audioRef}
        src={audio.url}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      <button
        type="button"
        className={styles.playbackCta}
        onClick={togglePlay}
        aria-label={
          isPlaying ? POST_AUDIO_TEXTS.ARIA_PAUSE : POST_AUDIO_TEXTS.ARIA_PLAY
        }
      >
        {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
      </button>

      <div
        className={styles.fallbackWaveSurfer}
        onClick={handleWaveClick}
        role="progressbar"
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <svg
          viewBox={`0 0 ${WAVE_BAR_HEIGHTS.length * 4.5} 48`}
          width="100%"
          height="48"
          preserveAspectRatio="none"
        >
          {WAVE_BAR_HEIGHTS.map((heightPercent, idx) => {
            const barWidth = 3;
            const barGap = 1.5;
            const x = idx * (barWidth + barGap);
            const barH = (heightPercent / 100) * 44;
            const y = (48 - barH) / 2;
            const percentAtBar = (idx / WAVE_BAR_HEIGHTS.length) * 100;
            const isPlayed = percentAtBar <= progressPercent;

            return (
              <rect
                key={idx}
                x={x}
                y={y}
                width={barWidth}
                height={barH}
                rx={1.5}
                ry={1.5}
                fill={isPlayed ? primaryColor : "#D1D5DB"}
              />
            );
          })}
        </svg>
      </div>

      <p className={styles.audioDuration}>
        {formatAudioTime(duration || currentTime)}
      </p>
    </div>
  );
};

const SingleAudioRow = ({
  audio,
  index,
  isRemovable,
  onRemove,
  primaryColor,
  AudioPlayerComponent,
}: ISingleAudioRowProps) => {
  const styles = useMemo(() => getAudioStyles(primaryColor), [primaryColor]);
  const waveSurferProps = useMemo(
    () => ({
      height: 48,
      waveColor: "#D1D5DB",
      progressColor: primaryColor || DEFAULT_PRIMARY_COLOR,
      barWidth: 3,
      barRadius: 8,
      barGap: 1.5,
    }),
    [primaryColor],
  );

  return (
    <div className={styles.audioRow}>
      {AudioPlayerComponent ? (
        <div className={styles.playerWrapper}>
          <AudioPlayerComponent
            audioUrl={audio.url}
            isReceivedMessage={true}
            playBtnClassName={styles.playbackCta}
            audioSurferClassName={styles.audioSurfer}
            audioDurationClassName={styles.audioDuration}
            waveSurferProps={waveSurferProps}
          />
        </div>
      ) : (
        <FallbackWaveformPlayer
          audio={audio}
          styles={styles}
          primaryColor={primaryColor}
        />
      )}

      {isRemovable && onRemove && (
        <button
          type="button"
          className={styles.removeButton}
          onClick={() => onRemove(index)}
          aria-label={POST_AUDIO_TEXTS.ARIA_REMOVE}
        >
          <CloseRoundedIcon />
        </button>
      )}
    </div>
  );
};

export const PostAudio = ({
  showAudio = true,
  audios,
  isRemovable,
  onRemove,
  classes,
  AudioPlayerComponent,
}: IPostAudioProps) => {
  const theme = useCreatorThemeContext();
  const primaryColor = theme?.colors?.color1 || DEFAULT_PRIMARY_COLOR;
  const styles = useMemo(() => getAudioStyles(primaryColor), [primaryColor]);

  if (!showAudio || !audios || checkIsArrayEmpty(audios)) return null;

  return (
    <div className={classNames(styles.container, classes?.container)}>
      {audios.map((audio, index) => (
        <SingleAudioRow
          key={audio.url || index}
          audio={audio}
          index={index}
          isRemovable={isRemovable}
          onRemove={onRemove}
          primaryColor={primaryColor}
          AudioPlayerComponent={AudioPlayerComponent}
        />
      ))}
    </div>
  );
};
